import { cloneDeep } from 'lodash-es';

export type ColumnState = {
  min?: number;
  max?: number;
  current: number;
  autoResize?: boolean;
};

const getNewColumnDiff = (column: ColumnState, newWidth: number): number => {
  let result = column.max ? Math.min(column.max, newWidth) : newWidth;
  result = Math.max(column.min || 0, result);
  return result - column.current;
};

export const resizeColumns = (
  columns: ColumnState[],
  columnIndex: number,
  newWidth: number,
): ColumnState[] => {
  const result = cloneDeep(columns);

  let availableLeadingDecrease = 0;
  for (let i = columnIndex; i >= 0; i -= 1) {
    const column = columns[i];
    if (!column.min) {
      availableLeadingDecrease = Number.MAX_VALUE;
      break;
    }
    availableLeadingDecrease += column.current - column.min;
  }
  let availableLeadingIncrease = 0;
  for (let i = columnIndex; i >= 0; i -= 1) {
    const column = columns[i];
    if (!column.max) {
      availableLeadingIncrease = Number.MAX_VALUE;
      break;
    }
    availableLeadingIncrease += column.max - column.current;
  }

  let availableTrailingDecrease = 0;
  for (let i = columnIndex + 1; i < result.length; i += 1) {
    const column = columns[i];
    if (!column.max) {
      availableTrailingDecrease = Number.MAX_VALUE;
      break;
    }
    availableTrailingDecrease += column.current - (column.min ?? 0);
  }

  let availableTrailingIncrease = 0;
  for (let i = columnIndex + 1; i < result.length; i += 1) {
    const column = columns[i];
    if (!column.max) {
      availableTrailingIncrease = Number.MAX_VALUE;
      break;
    }
    availableTrailingIncrease += column.max - column.current;
  }

  let trailingDecrease;
  if (newWidth > result[columnIndex].current) {
    trailingDecrease = Math.min(
      newWidth - result[columnIndex].current,
      availableLeadingIncrease,
      availableTrailingDecrease,
    );
  } else {
    trailingDecrease = Math.max(
      newWidth - result[columnIndex].current,
      -availableLeadingDecrease,
      -availableTrailingIncrease,
    );
  }

  let leadingDiff = 0;

  for (let i = columnIndex + 1; i < result.length; i += 1) {
    const diff = getNewColumnDiff(
      result[i],
      result[i].current - trailingDecrease,
    );
    result[i].current += diff;
    trailingDecrease += diff;
    leadingDiff += diff;
  }

  for (let i = columnIndex; i >= 0; i -= 1) {
    const diff = getNewColumnDiff(result[i], result[i].current - leadingDiff);
    result[i].current += diff;
    leadingDiff += diff;
  }

  return result;
};

export const redistributeWidth = (
  columns: ColumnState[],
  newWidth: number,
): ColumnState[] => {
  const result = cloneDeep(columns);

  for (let i = 0; i < result.length; i += 1) {
    // skip the adjusting of the size for auto resizable title
    // because it should be controlled by the length of the text
    if (result[i].autoResize) {
      continue;
    }
    // Trying to set column i width to the whole table width minus others columns width
    const restSize =
      newWidth -
      result.reduce(
        (acc, { current }, index) => acc + (index !== i ? current : 0),
        0,
      );

    const diff = getNewColumnDiff(result[i], restSize);
    result[i].current += diff;
  }

  return result;
};
